import React, { useEffect, useState } from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { Container, Grid, Paper, TextField } from '@mui/material';
import { Link } from 'react-router-dom';
import ICircuit from '../../models/ICircuit';
import CircuitServiceInstance from '../../services/CircuitService';
import { useTranslation } from 'react-i18next';
import { format } from 'util';
import axios from 'axios';
import OntimeBreadcrumb from '../common/OntimeBreadcrumb';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            maxWidth: '36ch',
            backgroundColor: theme.palette.background.paper,
        },
        inline: {
            display: 'inline',
        },
        content: {
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
        },
        container: {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
        },
        paper: {
            padding: theme.spacing(2),
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column'
        },
        appBarSpacer: theme.mixins.toolbar
    }));

interface ICircuitCardProps {
    key: string,
    circuit: ICircuit;
}

function CircuitCard(props: ICircuitCardProps) {
    const classes = useStyles();
    const { circuit } = props;
    const { t } = useTranslation("common");
    return (
        <Link to={`/circuits/${circuit.circuitId}`} >
            <ListItem alignItems="flex-start" >
                <ListItemAvatar>
                    <Avatar alt={circuit.name} src={circuit.logoUrl} />
                </ListItemAvatar>
                <ListItemText
                    primary={circuit.name}
                    secondary={
                        <React.Fragment>
                            <Typography
                                component="span"
                                variant="body2"
                                className={classes.inline}
                                color="textPrimary"
                            >
                                {circuit.location}
                            </Typography>
                            {format(t("circuit.length"), circuit.length)}
                        </React.Fragment>
                    }
                />
            </ListItem>
        </Link>);
}

function Circuits() {
    const [circuits, setCircuits] = useState<ICircuit[]>([]);
    const [nonFilteredCircuits, setNonFilteredCircuits] = useState<ICircuit[]>([]);
    const { t } = useTranslation("common");
    const classes = useStyles();

    useEffect(() => {
        const source = axios.CancelToken.source();
        CircuitServiceInstance.getCircuits(source.token).then((circuits: ICircuit[]) => {
            setCircuits(circuits);
            setNonFilteredCircuits(circuits);
        }).catch((error) => {
            if (!axios.isCancel(error)) {
                console.error('Failed to fetch circuits:', error);
            }
        });
        return () => {
            source.cancel();
        };
    }, []);

    const circuitFilterChanged = (event: any) => {
        const filterValue: string = event.target.value.toLowerCase();
        if (filterValue.length > 0 && nonFilteredCircuits) {
            const filteredCircuits = nonFilteredCircuits.filter(r => r.name.toLowerCase().indexOf(filterValue) >= 0);
            setCircuits(filteredCircuits);
        } else {
            setCircuits(nonFilteredCircuits);
        }
    }
    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <Container maxWidth="lg" className={classes.container}>
                <Grid container spacing={3}>
                    {/* Breadcrumb */}
                    <Grid item xs={12}>
                        <OntimeBreadcrumb nameMap={{}} current={t('mainList.circuits')} />
                    </Grid>
                    <Grid item xs={12}>
                        <Paper className={classes.paper}>
                            <TextField label={t("circuit.filter")} id="circuitfilter" onChange={circuitFilterChanged} />
                        </Paper>
                        <Paper className={classes.paper}>

                            <List className={classes.root} >
                                {
                                    circuits.map(function (circuitData) {
                                        return <CircuitCard key={circuitData.name} circuit={circuitData} />
                                    })
                                }
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </main>
    );
}

export default Circuits;